/**
 * Workflow Step Block: one step of a workflow as a card on the edgeless
 * canvas. The card itself comes from the workflow editor, which provides
 * `WorkflowStepBlockConfigExtension`; the block places it and makes it
 * selectable and movable like any other canvas block.
 */
import { Bound, type SerializedXYWH } from '@blocksuite/global/gfx';
import {
  BlockComponent,
  type GfxBlockComponent,
  toGfxBlockComponent,
} from '@blocksuite/std';
import { GfxViewInteractionExtension } from '@blocksuite/std/gfx';
import { css, html, nothing } from 'lit';
import { query } from 'lit/decorators.js';

import {
  type WorkflowStepBlockModel,
  WorkflowStepBlockSchema,
} from '../schemas/workflow-step-model.js';
import { StepGlide } from './step-glide.js';
import { WorkflowStepBlockConfigExtension } from './workflow-step-block-config.js';

export const AFFINE_WORKFLOW_STEP_CANVAS = 'affine-workflow-step-canvas';

/** Base of the canvas view; steps live on canvases only. */
export class WorkflowStepBlockComponent extends BlockComponent<WorkflowStepBlockModel> {
  override renderBlock() {
    return nothing;
  }
}

export class WorkflowStepCanvasBlockComponent extends toGfxBlockComponent(
  WorkflowStepBlockComponent
) {
  static override styles = css`
    affine-workflow-step-canvas {
      display: block;
    }
  `;

  /**
   * The block is as high as its card. The card's height follows its content,
   * so it is measured and handed to the workflow editor, which makes room for
   * it. A card that fills its box is not measured: the layout sets its size.
   */
  private readonly _cardResize = new ResizeObserver(([entry]) => {
    if (!entry || this.store.readonly) return;
    const height = Math.ceil(entry.borderBoxSize[0]?.blockSize ?? 0);
    if (height === 0 || height === Bound.deserialize(this.model.xywh).h) return;
    this._config.cardMeasured(this.model, height);
  });

  /** Where the step lay when a drag began. */
  private _dragFrom: SerializedXYWH | null = null;

  @query('.workflow-step-block')
  private accessor _card: HTMLElement | null = null;

  /** Moves the painted box to every place the step is given without a drag. */
  private _glide: StepGlide | null = null;

  private get _config() {
    return this.std.get(WorkflowStepBlockConfigExtension.identifier);
  }

  override connectedCallback() {
    super.connectedCallback();
    const glide = new StepGlide({
      model: this.model,
      view: this.ownerDocument.defaultView ?? window,
      place: () => {
        this.gfx.grid.update(this.model);
        this.refreshGfxTransform();
        this.gfx.selection.slots.transientGeometryUpdated.next(this.model.id);
      },
    });
    this._glide = glide;
    const subscription = this.model.propsUpdated.subscribe(({ key }) => {
      if (key === 'xywh') glide.changed();
    });
    this.disposables.add(() => {
      subscription.unsubscribe();
      glide.dispose();
      if (this._glide === glide) this._glide = null;
    });
  }

  /** Shows `xywh` instead of the stored box, e.g. the room for a dragged step; undefined goes back. */
  showPreview(xywh: SerializedXYWH | undefined) {
    this._glide?.show(xywh);
  }

  override onDragStart(...args: Parameters<GfxBlockComponent['onDragStart']>) {
    this._glide?.hold();
    this._dragFrom = this.model.xywh;
    super.onDragStart(...args);
  }

  override onDragEnd(...args: Parameters<GfxBlockComponent['onDragEnd']>) {
    super.onDragEnd(...args);
    this._glide?.release();
    const from = this._dragFrom;
    this._dragFrom = null;
    if (from !== null && from !== this.model.xywh) {
      this._config.moved(this.model);
    }
  }

  override firstUpdated() {
    if (this._card && !this._config.fillsBox(this.model)) {
      this._cardResize.observe(this._card);
    }
  }

  override disconnectedCallback() {
    this._cardResize.disconnect();
    super.disconnectedCallback();
  }

  override renderGfxBlock() {
    const config = this._config;
    // A card that does not fill its box decides the block's height itself.
    const height = config.fillsBox(this.model) ? 'height: 100%;' : '';
    return html`<div
      class="workflow-step-block"
      data-workflow-step=${this.model.id}
      style="width: 100%; ${height}"
    >
      ${config.renderCard(this.model)}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_WORKFLOW_STEP_CANVAS]: WorkflowStepCanvasBlockComponent;
  }
}

/** A step keeps the size of its card: it is moved, never rotated or resized. */
export const WorkflowStepBlockInteraction =
  GfxViewInteractionExtension<WorkflowStepCanvasBlockComponent>(
    WorkflowStepBlockSchema.model.flavour,
    {
      resizeConstraint: {
        lockRatio: true,
        allowedHandlers: [],
      },
      handleRotate: () => ({
        beforeRotate(context) {
          context.set({ rotatable: false });
        },
      }),
    }
  );
